import React, {PureComponent, useContext, useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {
    ComposedChart,
    Line,
    Label,
    Area,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Scatter,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts';
import {linearGradient, defs} from 'svg';
import {formatDateFloodShort} from "../utils/dateFunctions";
import {convertToPixels} from "../utils/pixelFunctions";
import ThresholdSelector from "./tresholdSelector";
import {DataContext} from "../contexts/DataContext";
import AdminSelector from "./adminSelector";
import {extractHierarchy} from "../utils/adm";
import {interpolateP} from "../utils/mathFunctions";
import {FLOOD_OVERLAY_FONT_SIZE, FLOOD_TEXT, INDEX_TO_BAND} from "../constants/constants";

function numberWithCommas(x) {
    return '$' + Math.round(x).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function PadObjectToArray(inputObject, pad_left = 1, pad_right = 1, repeat_value = 1) {
    // Extract the keys from the input object
    const keys = Object.keys(inputObject);

    // Get the number of rows
    const numRows = repeat_value + pad_left + pad_right;

    // Initialize the result array
    const resultArray = [];

    for (let i = 0; i < numRows; i++) {
        const newRow = {};
        for (const key of keys) {
            if (i > pad_left -1 && i <= numRows - pad_right - 1) {
                newRow[key] = inputObject[key];
            } else {
                newRow[key] = 0;
            }
        }
        resultArray.push(newRow);
    }

    return resultArray;
}


function mapValueToBand(value) {
    switch (value) {
        case 0:
            return "band_1";
        case 1:
            return "band_5";
        case 2:
            return "band_11";
        default:
            return "band_1";
    }
}

function recursivelyReplaceNumericalValues(obj) {
    if (typeof obj === 'number') {
        return [0, obj, 0];
    } else if (Array.isArray(obj)) {
        return obj.map((item) => recursivelyReplaceNumericalValues(item));
    } else if (typeof obj === 'object') {
        const newObj = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                newObj[key] = recursivelyReplaceNumericalValues(obj[key]);
            }
        }
        return newObj;
    } else {
        return obj;
    }
}


function padArray(data, padLeft = 1, padRight = 1, repeatCenter = 1, paddingValue = 0) {
    // Define the bands to be transformed
    const bandsToTransform = ['band_1', 'band_5', 'band_11'];

    // Initialize the result object
    const paddedData = { ...data };

    // Iterate through the data and apply padding
    for (const key in paddedData) {
        if (typeof paddedData[key] === 'object') {
            for (const region in paddedData[key]) {
                for (const band of bandsToTransform) {
                    const value = paddedData[key][region][band];
                    const paddedArray = [];

                    for (let i = 0; i < padLeft; i++) {
                        paddedArray.push(paddingValue);
                    }

                    for (let i = 0; i < repeatCenter; i++) {
                        paddedArray.push(value);
                    }

                    for (let i = 0; i < padRight; i++) {
                        paddedArray.push(paddingValue);
                    }

                    paddedData[key][region][band] = paddedArray;
                }
            }
        }
    }

    return paddedData;
}


function convertEconomicData(originalData) {
    const result = [];

    for (let i = 0; i < originalData.loss.length; i++) {
        result.push({
            loss: originalData.loss[i],
            p: 1.0 - originalData.p[i]
        });
    }

    return result;
}

/*const CustomizedAxisTick = (props) => {
    const { x, y, payload, unit } = props;

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} fontSize={14} textAnchor="end" fill="#666">
                {payload.value} {unit}
            </text>
        </g>
    );
};*/

/*const CustomizedAxisTick = ({ x, y, payload, dy, factor = null, unit = "" }) => {
    const { value } = payload;
    const adjustedValue = factor ? (value * factor).toString() + unit : value;

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={dy} fontSize={14} textAnchor="end" fill="#666">
                {adjustedValue}
            </text>
        </g>
    );
};*/

function transformToCustomFormat(value, decimalPlaces = 2) {
    // Check if the value is a number
    if (typeof value === 'number' && !isNaN(value)) {
        const absoluteValue = Math.abs(value);
        let result = 0;
        let suffix = '';

        if (absoluteValue >= 1e9) {
            result = (absoluteValue / 1e9).toFixed(decimalPlaces).replace(/\.?0+$/, '');
            suffix = 'G';
        }
        else if (absoluteValue >= 1e6) {
            result = (absoluteValue / 1e6).toFixed(decimalPlaces).replace(/\.?0+$/, '');
            suffix = 'M';
        } else if (absoluteValue >= 1e3) {
            result = (absoluteValue / 1e3).toFixed(decimalPlaces).replace(/\.?0+$/, '');
            suffix = 'K';
        } else {
            result = absoluteValue.toFixed(decimalPlaces).replace(/\.?0+$/, '');
        }

        return `${result} ${suffix}`;
    } else {
        // If the value is not a valid number, return it as is
        return value;
    }
}



    const CustomizedAxisTick = ({ x, y, payload, dy, factor = null, unit = "", position = "left" , decimalPlaces = 2}) => {
    const { value } = payload;
    const adjustedValue = transformToCustomFormat(value, decimalPlaces ).toString() + unit;
    //const adjustedValue = factor ? (value * factor).toString() + unit : value;
    console.log("adjustedValue", adjustedValue);

    // Determine the textAnchor based on the position parameter
    const textAnchor = position === "left" ? "end" : "start";

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={dy} fontSize={14} textAnchor={textAnchor} fill="#666">
                {adjustedValue}
            </text>
        </g>
    );
};


export default function FloodDayByDayChart(props) {

    const {countryCode, countryStochasticPopulationData, countryStochasticEconomicData, totalPopulation, totalEconomic} = props;

    const {
        selectedThreshold,
        selectedThresholdPop,
        selectedThresholdEco,
        selectedAdmin,
        updateSelectedAdmin,
        selectedAdmin1,
        updateSelectedAdmin1,
        adm1Options,
        updateAdm1Options,
    } = useContext(DataContext);

    const [options, setOptions] = useState(null);

    const data = props.data.day_by_day.map((day) => {
        return {
            day: formatDateFloodShort(props.data.start_date, day.day),
            severity_index_1m: day.stats.severity_index_1m,
            flooded_area_km2: day.stats.flooded_area_km2, // * (0.0027 * 0.0027), //TODO: replace by backend, after correction!!! day.stats.flooded_area_km2,
            max: day.stats.max,
            band_1: day.adm0[0].band_1,
            band_5: day.adm0[0].band_5,
            band_11: day.adm0[0].band_11,
            eco_band_1: day.adm0_eco ? day.adm0_eco[0].band_1 : null,
            eco_band_5: day.adm0_eco ? day.adm0_eco[0].band_5 : null,
            eco_band_11: day.adm0_eco ? day.adm0_eco[0].band_11 : null,
        }
    }
    );

    useEffect(() => {
        updateSelectedAdmin('adm0');
        updateSelectedAdmin1(null);
        setOptions(null);
    }, [countryCode]);

    useEffect(() => {
        if (countryStochasticPopulationData[countryCode]) {
            const newAdm1Options = extractHierarchy(countryStochasticPopulationData[countryCode])[`thresh_${selectedThresholdPop}`]['adm1'];
            setOptions(newAdm1Options);
            //updateAdm1Options(newAdm1Options);
            console.log("newAdm1Options", newAdm1Options)
            console.log("adm1Options", adm1Options)
        }
    }, [countryCode, selectedAdmin]);


/*    let adm1Options = [];
    if (countryStochasticPopulationData[countryCode] && selectedAdmin === "adm1") {
        adm1Options = Object.keys(countryStochasticPopulationData[countryCode][`thresh_${selectedThreshold}`][`${selectedAdmin}`]);
        updateSelectedAdmin1(adm1Options);
    }*/

    let stochasticPopulationData;

    if (countryStochasticPopulationData[countryCode]) {
        if (countryStochasticPopulationData[countryCode]) {
            if (selectedAdmin === "adm1") {
                if (selectedAdmin1) {
                    stochasticPopulationData = countryStochasticPopulationData[countryCode][`thresh_${selectedThresholdPop}`][`${selectedAdmin}`][`${selectedAdmin1}`]
                } else {
                    stochasticPopulationData = countryStochasticPopulationData[countryCode][`thresh_${selectedThresholdPop}`][`${selectedAdmin}`][options[0]]
                }
            } else {
                stochasticPopulationData = countryStochasticPopulationData[countryCode][`thresh_${selectedThresholdPop}`][`${selectedAdmin}`]
            }
        }
    }

    useEffect(() => {
        if (countryStochasticPopulationData[countryCode]) {
            if (selectedAdmin === "adm1") {
                if (selectedAdmin1) {
                    stochasticPopulationData = countryStochasticPopulationData[countryCode][`thresh_${selectedThresholdPop}`][`${selectedAdmin}`][`${selectedAdmin1}`]
                } else {
                    stochasticPopulationData = countryStochasticPopulationData[countryCode][`thresh_${selectedThresholdPop}`][`${selectedAdmin}`][options[0]]
                }
            } else {
                stochasticPopulationData = countryStochasticPopulationData[countryCode][`thresh_${selectedThresholdPop}`][`${selectedAdmin}`]
            }
        }
    }, [countryCode, selectedAdmin, selectedAdmin1]);


    let stochasticEconomicData;
    let closestP;
    let economicPeak;

    if (countryStochasticEconomicData[countryCode]) {
        if (countryStochasticEconomicData[countryCode]) {
            if (selectedAdmin === "adm1") {
                if (selectedAdmin1 && (selectedAdmin1 in countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`])) {
                    stochasticEconomicData = convertEconomicData(countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`][`${selectedAdmin1}`])
                    if (totalEconomic) {
                        economicPeak = totalEconomic[selectedAdmin][selectedAdmin1][`band_${INDEX_TO_BAND[selectedThresholdEco]}`];
                    }
                } else if (options && (options[0] in countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`])) {
                    stochasticEconomicData = convertEconomicData(countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`][options[0]])
                } else {
                    stochasticEconomicData = convertEconomicData(countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`adm0`])
                }
            } else if (selectedAdmin === "adm0") {
                if (totalEconomic) {
                    economicPeak = totalEconomic[selectedAdmin][`band_${INDEX_TO_BAND[selectedThresholdEco]}`];
                }
                stochasticEconomicData = convertEconomicData(countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`])
            }
        }
        closestP = interpolateP(stochasticEconomicData, economicPeak);
        console.log("closestP", closestP)
    }

    useEffect(() => {
        if (countryStochasticEconomicData[countryCode]) {
            if (selectedAdmin === "adm1") {
                if (selectedAdmin1 && (selectedAdmin1 in countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`])) {
                    stochasticEconomicData = convertEconomicData(countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`][`${selectedAdmin1}`])
                    if (totalEconomic) {
                        economicPeak = totalEconomic[selectedAdmin][selectedAdmin1][`band_${INDEX_TO_BAND[selectedThresholdEco]}`];
                    }
                } else if (options && (options[0] in countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`])) {
                    stochasticEconomicData = convertEconomicData(countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`][options[0]])
                } else {
                    stochasticEconomicData = convertEconomicData(countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`adm0`])
                }
            } else if (selectedAdmin === "adm0") {
                if (totalEconomic) {
                    economicPeak = totalEconomic[selectedAdmin][`band_${INDEX_TO_BAND[selectedThresholdEco]}`];
                }
                stochasticEconomicData = convertEconomicData(countryStochasticEconomicData[countryCode][`thresh_${selectedThresholdEco}`][`${selectedAdmin}`])
            }
            closestP = interpolateP(stochasticEconomicData, economicPeak);
            console.log("closestP", closestP)
        }
        }, [countryCode, selectedAdmin, selectedAdmin1]);

    const populationData = PadObjectToArray(totalPopulation)
    // const populationData = recursivelyReplaceNumericalValues(totalPopulation)
    // const index = mapValueToBand(selectedThresholdPop);
    console.log("populationData", populationData)

    const economic_data_available = data.every((item) => item.eco_band_1 !== null && item.eco_band_5 !== null && item.eco_band_11 !== null);

    // Adding data points before and after the event to get smoother graphs

/*    // Add data point at the beginning with all values as zero
    data.unshift({
        day: formatDateFloodShort(props.data.start_date, -1),
        severity_index_1m: 0,
        flooded_area_km2: 0,
        max: 0,
        band_1: 0,
        band_5: 0,
        band_11: 0,
    });

    // Add data point at the end with all values as zero except for the incremented day
    //const lastDay = props.data[props.data.length - 1].day;
    data.push({
        day: formatDateFloodShort(props.data.start_date, props.data.day_by_day.length+1),
        severity_index_1m: 0,
        flooded_area_km2: 0,
        max: 0,
        band_1: 0,
        band_5: 0,
        band_11: 0,
    });*/

    const width = convertToPixels(props.width);
    const height = convertToPixels(props.height) * 2/3;  // since there are 3 elements (2 graphs + 1 text)

    const textFontSize = 12

    const { t } = useTranslation();

    return (
        <div className={'flood-overlay'}>
{/*            <ComposedChart
                width={width}
                height={height}
                data={data}
                margin={{
                    top: 20,
                    right: 60,
                    bottom: 20,
                    left: 10,
                }}
            >
                <defs>
                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorRv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="red" stopOpacity={0.9}/>
                        <stop offset="95%" stopColor="red" stopOpacity={0.7}/>
                    </linearGradient>
                </defs>
                <CartesianGrid stroke="#f5f5f5" />
                <XAxis dataKey="day" />  scale="band"
                <YAxis yAxisId="left" >
                    <Label
                        style={{
                            textAnchor: "middle",
                            fontSize: "75%",
                            //fill: "white",
                        }}
                        angle={270}
                        value={'Flooded area (km2)'} //{t('adm_overlay.chart.left_axis.title')}
                    />
                </YAxis>
                <YAxis yAxisId="right" orientation="right" value={'Severity Index'} /> //tickFormatter={numberWithCommas}/>
                <Tooltip placement={'top'} formatter={(value) => new Intl.NumberFormat('en').format(value)}/>
                <Legend />
                                <Bar yAxisId="right" dataKey="counter_year" barSize={20} fill="#413ea0" fillOpacity={0.2}/>
            <Scatter yAxisId="right" dataKey="counter_adm" fill="red" fillOpacity={0.7}/>
                            <Bar yAxisId="left" dataKey="flooded_area_km2" name={t('adm_overlay.chart.bottom_axis.storms_per_year.title')} barSize={10} fill="#8884d8" stroke="#8884d8" fill="url(#colorUv)"/>
            <Bar yAxisId="left"  dataKey="max" name={t('adm_overlay.chart.bottom_axis.storms_per_adm.title')} barSize={10} fill="#8884d8" stroke="#8884d8" fill="url(#colorUv)"/>
                <Area yAxisId="left" type="monotone" dataKey="flooded_area_km2" name="Flooded area (km2)" fill="red" stroke="#82ca9d" fill="url(#colorPv)"/>
                <Line yAxisId="right" type="monotone" dataKey="severity_index_1m" name="Severity index" fill="red" stroke='red' fill="url(#colorRv)" />
                <Scatter dataKey="cnt" fill="red" />
            </ComposedChart>*/}

            <ResponsiveContainer width={width} height={height}>
                <ComposedChart
                    width={width}
                    height={height}
                    data={data}
                    //syncId="anyId"
                    margin={{
                        top: 20,
                        right: 20,
                        bottom: 20,
                        left: 20,
                    }}
                >
                    <defs>
                        <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorRv" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="red" stopOpacity={0.9}/>
                            <stop offset="95%" stopColor="red" stopOpacity={0.7}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis dataKey="day" tick={<CustomizedAxisTick dy={16}/>}/>
                    <YAxis
                        yAxisId="left"
                        tick={<CustomizedAxisTick dy={8} factor={0.001}/>}
                        tickFormatter={(value) => value.toFixed(4)}
                        type="number"
                        label={{ value: 'Flooded area (km2)', angle: -90, position: 'insideBottomLeft', fontSize: FLOOD_OVERLAY_FONT_SIZE, textAnchor: 'middle'}}
                    />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={<CustomizedAxisTick dy={8} factor={1} position={"right"}/>}
                        tickFormatter={(value) => value.toFixed(4)}
                        type="number"
                        label={{ value: 'Severity index', angle: -270, position: 'insideBottomRight', fontSize: FLOOD_OVERLAY_FONT_SIZE}}
                    />
                    <Tooltip labelStyle={{fontSize: textFontSize}} contentStyle={{fontSize: textFontSize}} placement={'top'} formatter={(value) => new Intl.NumberFormat('en').format(value)} />
                    <Legend wrapperStyle={{fontSize: textFontSize}}/>
                    <Area yAxisId="left" type="monotone" dataKey="flooded_area_km2" name="Flooded area (km2)" fill="red" stroke="#82ca9d" fill="url(#colorPv)"/>
                    <Line yAxisId="right" type="monotone" dataKey="severity_index_1m" name="Severity index" fill="red" stroke='red' fill="url(#colorRv)" />
                </ComposedChart>
            </ResponsiveContainer>

            <ResponsiveContainer width={width} height={height}>
                <ComposedChart
                    width={width}
                    height={height}
                    data={data}
                    //syncId="anyId"
                    margin={{
                        top: 20,
                        right: 20,
                        bottom: 20,
                        left: 20,
                    }}
                >
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis dataKey="day" tick={<CustomizedAxisTick dy={16}/>}/>
                    <YAxis yAxisId="left" tick={<CustomizedAxisTick dy={8} />} type="number" label={{ value: 'Population in flooded area', angle: -90, position: 'insideBottomLeft', fontSize: FLOOD_OVERLAY_FONT_SIZE}} />
                    <YAxis yAxisId="right" orientation="right" tick={<CustomizedAxisTick dy={8} unit={'$'} position={"right"}/>} type="number" label={{ value: 'Losses in flooded area', angle: -270, position: 'insideBottomRight', fontSize: FLOOD_OVERLAY_FONT_SIZE}} />
                    <Tooltip labelStyle={{fontSize: textFontSize}} contentStyle={{fontSize: textFontSize}} placement={'top'} formatter={(value) => new Intl.NumberFormat('en').format(value)} />
                    <Legend wrapperStyle={{fontSize: textFontSize}}/>
                    {/*<Line yAxisId="left" type="monotone" dataKey="band_1" name="0.001m" stroke="blue" />*/}
                    <Line yAxisId="left" type="monotone" dataKey="band_5" name="population exposed" stroke="orange" />
                    <Line yAxisId="right" type="monotone" dataKey="eco_band_5" name="economic losses ($)" stroke="blue" />
                    {/*<Line yAxisId="left" type="monotone" dataKey="band_11" name="0.5m" stroke="purple" />*/}
                </ComposedChart>
            </ResponsiveContainer>

{/*            <ResponsiveContainer width={width} height={height}>
                <ComposedChart
                    width={width}
                    height={height}
                    data={data}
                    //syncId="anyId"
                    margin={{
                        top: 20,
                        right: 20,
                        bottom: 20,
                        left: 20,
                    }}
                >
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis dataKey="day" tick={<CustomizedAxisTick dy={16}/>}/>
                    <YAxis yAxisId="left" tick={<CustomizedAxisTick dy={8} factor={0.000001} unit={'M$'}/>} type="number" label={{ value: 'Losses in flooded area', angle: -90, position: 'insideBottomLeft', fontSize: '75%'}} />
                    <Tooltip labelStyle={{fontSize: textFontSize}} contentStyle={{fontSize: textFontSize}} placement={'top'} formatter={(value) => '$' + new Intl.NumberFormat('en').format(value)} />
                    <Legend wrapperStyle={{fontSize: textFontSize}}/>
                    <Line yAxisId="left" type="monotone" dataKey="eco_band_1" name="0.001m" stroke="orange" />
                    <Line yAxisId="left" type="monotone" dataKey="eco_band_5" name="0.2m" stroke="blue" />
                    <Line yAxisId="left" type="monotone" dataKey="eco_band_11" name="0.5m" stroke="purple" />
                </ComposedChart>
            </ResponsiveContainer>*/}

{/*            {(stochasticPopulationData || stochasticEconomicData) &&
                <div>
                    <ThresholdSelector />
                    <AdminSelector options={options}/>
                </div>
            }*/}

            {stochasticPopulationData &&
                <div>
                    <ThresholdSelector
                        data={countryStochasticPopulationData[countryCode]}
                        type={'population'}
                    />
                    <AdminSelector options={options}/>
                </div>
            }

            {
                stochasticPopulationData &&
                <ResponsiveContainer width={width*0.8} height={height}>
                    <ComposedChart
                        width={width * 0.8}
                        height={height}
                        data={selectedAdmin === 'adm0' ? totalPopulation[selectedAdmin] : totalPopulation[selectedAdmin][selectedAdmin1]} //populationData[selectedAdmin]
                        //syncId="anyId"
                        margin={{
                            top: 20,
                            right: 20,
                            bottom: 20,
                            left: 20,
                        }}
                    >
                        <CartesianGrid stroke="#f5f5f5" />
                        <XAxis dataKey="day" tick={<CustomizedAxisTick dy={16}/>}/>
                        <YAxis yAxisId="left" tick={<CustomizedAxisTick dy={8} />} domain={[0, stochasticPopulationData[100] * 1.05]} type="number" label={{ value: 'Population in flooded area', angle: -90, position: 'insideBottomLeft', fontSize: FLOOD_OVERLAY_FONT_SIZE}} />
                        <Tooltip labelStyle={{fontSize: textFontSize}} contentStyle={{fontSize: textFontSize}} placement={'top'} formatter={(value) => new Intl.NumberFormat('en').format(value)} />
                        <Legend wrapperStyle={{fontSize: textFontSize}}/>
                        <Bar
                            yAxisId="left"
                            dataKey={`band_${INDEX_TO_BAND[selectedThresholdPop]}`}
                            name={FLOOD_TEXT[selectedThresholdPop]}
                            barSize={20}
                            fill="#413ea0"
                        />
                        <ReferenceLine yAxisId="left" y={stochasticPopulationData[2]} label={{ value: '2 Years', fill: 'goldenrod', position: 'insideBottomRight'}}  stroke="goldenrod" />
                        <ReferenceLine yAxisId="left" y={stochasticPopulationData[5]} label={{ value: '5 Years', fill: 'darkorange', position: 'insideBottomRight'}} stroke="darkorange" />
                        <ReferenceLine yAxisId="left" y={stochasticPopulationData[20]} label={{ value: '20 Years', fill: 'red', position: 'insideBottomRight'}} stroke="red" />
                        <ReferenceLine yAxisId="left" y={stochasticPopulationData[100]} label={{ value: '100 Years', fill: 'purple', position: 'insideBottomRight'}} stroke="purple" />
                    </ComposedChart>
                </ResponsiveContainer>
            }

            {stochasticEconomicData &&
                <div>
                    <ThresholdSelector
                        data={countryStochasticEconomicData[countryCode]}
                        type={'economic'}
                    />
                    <AdminSelector options={options}/>
                </div>
            }

            {
                stochasticEconomicData &&
                <ResponsiveContainer width={width*0.8} height={height}>
                    <ComposedChart
                        width={width * 0.8}
                        height={height}
                        data={stochasticEconomicData}
                        //syncId="anyId"
                        margin={{
                            top: 20,
                            right: 20,
                            bottom: 20,
                            left: 20,
                        }}
                    >
                        <CartesianGrid stroke="#f5f5f5" />
                        <XAxis
                            scale="auto"
                            domain={[0, totalEconomic ? totalEconomic[selectedAdmin]['band_1']: Math.max(stochasticEconomicData['loss'])]}
                            dataKey="loss"
                            type="number"
                            label={{ value: 'Loss', position: 'bottom', fontSize: FLOOD_OVERLAY_FONT_SIZE}}
                            tick={<CustomizedAxisTick dy={8} unit={'$'} position={"center"}/>}
                        />
                        <YAxis
                            yAxisId="left"
                            //domain={[0.0, 1.0]}
                            tick={<CustomizedAxisTick dy={8} />}
                            type="number"
                            label={{
                                value: 'Loss Exceedance Probability',
                                offset: 0,
                                angle: -90,
                                position: 'insideBottomLeft',
                                fontSize: FLOOD_OVERLAY_FONT_SIZE
                            }}
                        />
                        <Line yAxisId="left" type="monotone" dataKey="p" stroke="orange" strokeWidth={4} dot={false} />
                        {totalEconomic && closestP &&
                            <ReferenceLine yAxisId="left" segment={[{ x: selectedAdmin === 'adm0' ? totalEconomic[selectedAdmin][`band_${INDEX_TO_BAND[selectedThresholdEco]}`] : totalEconomic[selectedAdmin][selectedAdmin1][`band_${INDEX_TO_BAND[selectedThresholdEco]}`], y: 0 }, { x: selectedAdmin === 'adm0' ? totalEconomic[selectedAdmin][`band_${INDEX_TO_BAND[selectedThresholdEco]}`] : totalEconomic[selectedAdmin][selectedAdmin1][`band_${INDEX_TO_BAND[selectedThresholdEco]}`], y: closestP }]} stroke="blue" strokeWidth={6} label={{ value: 'Flood event', angle: -90, position: 'left', fontSize: '75%', fill: 'blue', offset: 10}} />
                        }
                        <Tooltip labelStyle={{fontSize: textFontSize}} contentStyle={{fontSize: textFontSize}} placement={'top'} formatter={(value) => new Intl.NumberFormat('en').format(value)} />
                        <Legend wrapperStyle={{fontSize: textFontSize}}/>
                    </ComposedChart>
                </ResponsiveContainer>
            }

        </div>
    );
}
